import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import NoAccess from './components/NoAccess';
import MaintenanceGate from './components/maintenance/MaintenanceGate';
import ProtectedRoute, { Loading, RequirePermission } from './components/ProtectedRoute';
import TenantLayout from './components/TenantLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { adminUrl, goTo, parseHost, routeAfterLogin } from './lib/host';
import AcceptInvite from './pages/AcceptInvite';
import { BASIC_PLAN, ENTERPRISE_PLAN, PREMIUM_PLAN } from './lib/plans';
import BasicRoutes from './pages/basic/BasicRoutes';
import CreateOrganization from './pages/CreateOrganization';
import CustomerDashboard from './pages/CustomerDashboard';
import ForgotPassword from './pages/ForgotPassword';
import Login from './pages/Login';
import Members from './pages/Members';
import Payment from './pages/Payment';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import SelectOrganization from './pages/SelectOrganization';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import TenantOverview from './pages/TenantOverview';


function AppHome() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (session) routeAfterLogin(session, navigate);
    else navigate('/login', { replace: true });
  }, [loading, session, navigate]);
  return <Loading />;
}

/** The old Platform Console moved into the Super Admin panel: old links open the matching page there. */
function ToSuperAdmin({ path }) {
  useEffect(() => { goTo(adminUrl(path)); }, [path]);
  return <Loading />;
}

/** app.<root>: authentication, onboarding and the organization picker. */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/select-org" element={<ProtectedRoute><SelectOrganization /></ProtectedRoute>} />
      <Route path="/create-org" element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} />
      <Route path="/platform" element={<ToSuperAdmin path="/" />} />
      <Route path="/platform/database" element={<ToSuperAdmin path="/settings/database" />} />
      <Route path="*" element={<AppHome />} />
    </Routes>
  );
}

function Overview() {
  const { can } = useTenant();
  return can('stats.read') ? <TenantOverview /> : <CustomerDashboard />;
}

// Plans with their own dashboard app, served by the frontend container under a path on the company's address.
const PLAN_DASHBOARDS = {
  [PREMIUM_PLAN]: { path: '/premium/', name: 'Premium', folder: 'premium' },
  [ENTERPRISE_PLAN]: { path: '/enterprise/', name: 'Enterprise', folder: 'enterprices' },
};

/**
 * Opens the company's plan dashboard. The development server doesn't serve those apps, so it would land back here:
 * say so instead of redirecting in a loop.
 */
function OpenPlanDashboard({ path, name, folder }) {
  const servedHere = window.location.pathname.startsWith(path);
  useEffect(() => {
    if (!servedHere) window.location.replace(path);
  }, [servedHere, path]);
  return servedHere
    ? <NoAccess title={`${name} dashboard not available here`} message={`It is served by the frontend container; run it with docker compose, or \`npm run dev\` in ${folder}/.`} />
    : <Loading />;
}

/** <slug>.<root>: the company's own login page (the address in its registration email), then everything inside it. */
function CompanyHost({ slug }) {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<TenantProvider slug={slug}><TenantRoutes /></TenantProvider>} />
    </Routes>
  );
}

/** Everything inside one organization. */
function TenantRoutes() {
  const { user } = useAuth();
  const { status, message, slug, tier, onboarding, tenant } = useTenant();
  // No user means the session just ended; TenantProvider is redirecting to login.
  if (!user || status === 'loading') return <Loading />;
  if (status !== 'ready') return <NoAccess title={`Can't open "${slug}"`} message={message} />;
  if (onboarding === 'choose_plan' || onboarding === 'payment_pending') {
    // A new company (customer approval): its owner chooses a plan and pays; the server allows nothing else until a
    // TeleCalX admin approves it.
    return (
      <Routes>
        <Route path="/subscription" element={<RequirePermission perm="billing.manage"><Subscription /></RequirePermission>} />
        <Route path="/payment" element={<RequirePermission perm="billing.manage"><Payment /></RequirePermission>} />
        <Route path="*" element={onboarding === 'payment_pending'
          ? <Navigate to="/payment" replace state={{ plan: tenant.plan }} /> : <Navigate to="/subscription" replace />} />
      </Routes>
    );
  }
  if (tier === BASIC_PLAN) return <BasicRoutes />;
  const planDashboard = PLAN_DASHBOARDS[tier];
  if (planDashboard) {
    // Choosing another plan stays here; everything else is the plan's own dashboard.
    return (
      <Routes>
        <Route path="/subscription" element={<RequirePermission perm="billing.manage"><Subscription /></RequirePermission>} />
        <Route path="/payment" element={<RequirePermission perm="billing.manage"><Payment /></RequirePermission>} />
        <Route path="*" element={<OpenPlanDashboard {...planDashboard} />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/subscription" element={<RequirePermission perm="billing.manage"><Subscription /></RequirePermission>} />
      <Route path="/payment" element={<RequirePermission perm="billing.manage"><Payment /></RequirePermission>} />
      <Route element={<TenantLayout />}>
        <Route index element={<Overview />} />
        <Route path="members" element={<Members />} />
        <Route path="settings" element={<RequirePermission perm="settings.write"><Settings /></RequirePermission>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const host = parseHost();
  return (
    <Router>
      <AuthProvider>
        {host.kind === 'tenant' ? (
          <MaintenanceGate>
            <CompanyHost slug={host.slug} />
          </MaintenanceGate>
        ) : (
          // Every page waits during maintenance, sign-in too. The team signs in from a browser that opened the developer
          // link (Super Admin › Maintenance Mode), which the maintenance check lets through.
          <MaintenanceGate>
            <AppRoutes />
          </MaintenanceGate>
        )}
      </AuthProvider>
    </Router>
  );
}
